// src/Pages/Hmo/ProductDetails.js
// ✅ FIXED: Uses your exact backend response format with Hospitals array
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

function ProductDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  
  // ✅ State for tracking params including agent attribution
  const [companyId, setCompanyId] = useState(null);
  const [linkId, setLinkId] = useState(null);
  const [productId, setProductId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  
  // ✅ Accordion state
  const [openSections, setOpenSections] = useState({
    about: false,
    benefits: false,
    howItWorks: false,
    hospitals: false
  });

  // ✅ FSL Brand Color - ORANGE
  const BRAND_COLOR = '#F89423';
  const BRAND_COLOR_LIGHT = '#FFF8F0';
  const BRAND_COLOR_BORDER = '#FDE4C8';
  const BRAND_COLOR_HOVER = '#E0851F';

  const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

  // ✅ AGGRESSIVE: Extract agentId from ALL possible sources
  const extractAgentId = () => {
    const fromUrl = searchParams.get('agent');
    if (fromUrl && fromUrl.trim() !== '') {
      console.log('✅ Agent from URL:', fromUrl);
      return fromUrl.trim();
    }
    
    try {
      const storageKeys = ['agentSession', 'userSession', 'hmoSession'];
      for (const key of storageKeys) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const session = JSON.parse(raw);
          const candidates = [
            session.agentId, session.agent_id, 
            session.sessionId, session.session_id
          ];
          const found = candidates.find(id => id && id.trim() !== '');
          if (found) {
            console.log(`✅ Agent from localStorage[${key}]:`, found);
            return found.trim();
          }
        }
      }
    } catch (err) {
      console.warn('⚠️ Failed to parse localStorage:', err);
    }
    
    try {
      const referrer = new URL(document.referrer);
      const fromReferrer = referrer.searchParams.get('agent');
      if (fromReferrer && fromReferrer.trim() !== '') {
        console.log('✅ Agent from referrer:', fromReferrer);
        return fromReferrer.trim();
      }
    } catch (err) {}
    
    console.warn('❌ AgentId NOT FOUND in any source');
    return null;
  };

  // ✅ HELPER: Parse benefits string into array
  const parseBenefits = (benefits) => {
    if (!benefits) return [];
    if (Array.isArray(benefits)) return benefits;
    
    if (typeof benefits === 'string') {
      try {
        const parsed = JSON.parse(benefits);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return benefits
          .split(/[\n,;•\-\*]+/)
          .map(item => item.trim())
          .filter(item => item.length > 0);
      }
    }
    return [];
  };

  // ✅ HELPER: Parse steps string into array
  const parseSteps = (steps) => {
    if (!steps) return [];
    if (Array.isArray(steps)) return steps;
    
    if (typeof steps === 'string') {
      try {
        const parsed = JSON.parse(steps);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return steps
          .split(/[\n,;•\-\*]+/)
          .map(item => item.trim())
          .filter(item => item.length > 0);
      }
    }
    return [];
  };

  // ✅ HELPER: Extract hospital names from Hospitals array (your format)
  const parseHospitals = (hospitals) => {
    if (!hospitals || !Array.isArray(hospitals)) return [];
    
    // ✅ Deduplicate hospitals by name
    const seen = new Set();
    return hospitals
      .map(h => h.Name || h.name || h.hospitalName || h.HospitalName || 'Partner Hospital')
      .filter(name => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      });
  };

  // ✅ Fetch product details from backend on mount
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const company = searchParams.get('company');
        const link = searchParams.get('link');
        const productParam = searchParams.get('product');
        const agent = extractAgentId();
        
        if (!company || !link || !productParam) {
          throw new Error('Missing required parameters: company, link, or product');
        }
        
        setCompanyId(company);
        setLinkId(link);
        setProductId(productParam);
        setAgentId(agent);
        
        console.log('📍 Fetching product details for:', { company, link, product: productParam, agent });
        
        // ✅ Call your endpoint: POST /Settings/links/company/{companyId}
        const response = await authenticatedFetch(
          `${API_BASE}/Settings/links/company/${company}?userType=hmo`,
          'POST',
          {}
        );
        
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to fetch links');
        }
        
        // ✅ Find the specific link by linkId
        const links = response.data || [];
        const targetLink = links.find(l => l.linkId === link);
        
        if (!targetLink) {
          throw new Error(`Link not found: ${link}`);
        }
        
        // ✅ Find the specific product by productId (handle string/number)
        const bundledProducts = targetLink.bundledProducts || [];
        const targetProduct = bundledProducts.find(p => 
          String(p.Id || p.id) === String(productParam)
        );
        
        if (!targetProduct) {
          throw new Error(`Product not found: ${productParam}`);
        }
        
        // ✅ Format product using YOUR exact response format
        const formattedProduct = {
          id: targetProduct.Id || targetProduct.id,
          name: targetProduct.Name || targetProduct.name || 'Product',
          provider: targetProduct.providerName || targetProduct.ProviderName || 'Bastion Health Limited',
          price: targetProduct.MinimumPremium ?? targetProduct.minimumPremium ?? 35000,
          period: 'Month',
          
          // ✅ About: Use Benefits field or fallback
          about: targetProduct.Description || targetProduct.description || 
                 (typeof (targetProduct.Benefits || targetProduct.benefits) === 'string' 
                   ? `Comprehensive health coverage: ${targetProduct.Benefits || targetProduct.benefits}`
                   : `Comprehensive ${targetProduct.CategoryName || targetProduct.categoryName || 'health'} coverage`),
          
          // ✅ Benefits: Parse from Benefits field (your format: string)
          benefits: parseBenefits(targetProduct.Benefits || targetProduct.benefits).length > 0
            ? parseBenefits(targetProduct.Benefits || targetProduct.benefits)
            : [
                'Basic outpatient care',
                'Emergency services',
                'General consultations',
                'Basic laboratory tests',
                'Prescription medications'
              ],
          
          // ✅ How It Works: Parse from HowItWorks field
          howItWorks: parseSteps(targetProduct.HowItWorks || targetProduct.howItWorks).length > 0
            ? parseSteps(targetProduct.HowItWorks || targetProduct.howItWorks)
            : [
                'Register and complete your enrollment',
                'Receive your insurance ID card',
                'Choose from our network of healthcare providers',
                'Schedule appointments with participating doctors',
                'Present your ID card during visits'
              ],
          
          // ✅ Hospitals: Extract from YOUR Hospitals array format with deduplication
          hospitals: parseHospitals(targetProduct.Hospitals || targetProduct.hospitals).length > 0
            ? parseHospitals(targetProduct.Hospitals || targetProduct.hospitals)
            : [
                'Bastion Health Medical Center - Lagos',
                'Bastion Specialist Hospital - Abuja',
                'Bastion Care Clinic - Port Harcourt',
                'Partner hospitals across Nigeria'
              ],
          
          _original: targetProduct
        };
        
        console.log('✅ Loaded product details:', formattedProduct.name);
        console.log('✅ Hospitals (deduplicated):', formattedProduct.hospitals);
        setProduct(formattedProduct);
        
      } catch (err) {
        console.error('💥 Error fetching product details:', err);
        setError(err.message || 'Failed to load product details');
        toast.error(err.message || 'Could not load product');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductDetails();
  }, [searchParams]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleContinue = () => {
    setLoading(true);
    
    setTimeout(() => {
      // ✅ Build query string preserving ALL params including agent
      const queryParams = new URLSearchParams();
      if (companyId) queryParams.set('company', companyId);
      if (linkId) queryParams.set('link', linkId);
      if (productId) queryParams.set('product', productId);
      
      // ✅ CRITICAL: Preserve agent attribution
      if (agentId) {
        queryParams.set('agent', agentId);
        console.log('✅ Preserving agent attribution:', agentId);
      } else {
        console.warn('⚠️ No agentId to preserve');
        const lastResort = extractAgentId();
        if (lastResort) {
          queryParams.set('agent', lastResort);
          console.log('✅ Last-resort agent attribution:', lastResort);
        }
      }
      
      const queryString = queryParams.toString();
      console.log('🔗 Navigating with params:', queryString);
      
      const redirectPath = queryString ? `/insurance/plan-owner?${queryString}` : '/insurance/plan-owner';
      navigate(redirectPath);
    }, 800);
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  const formatPrice = (price) => {
    return `₦${Number(price).toLocaleString()}`;
  };

  // ✅ Loading State
  if (loading && !product) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem', color: BRAND_COLOR }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading product details...</p>
        </div>
      </div>
    );
  }

  // ✅ Error State
  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center p-4" style={{ maxWidth: '400px' }}>
          <div className="rounded-circle bg-danger bg-opacity-10 text-danger d-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px', fontSize: '24px' }}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h5 className="fw-semibold mb-2">Error Loading Product</h5>
          <p className="text-muted mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn"
            style={{ backgroundColor: BRAND_COLOR, color: 'white', border: 'none', padding: '8px 24px', borderRadius: '8px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ✅ Empty State
  if (!product) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center p-4" style={{ maxWidth: '400px' }}>
          <div className="rounded-circle bg-light text-muted d-flex align-items-center justify-content-center mb-3" style={{ width: '64px', height: '64px', fontSize: '24px' }}>
            <i className="fas fa-box-open"></i>
          </div>
          <h5 className="fw-semibold mb-2">Product Not Found</h5>
          <p className="text-muted mb-4">The requested product could not be found.</p>
          <button onClick={handleClose} className="btn btn-outline-secondary">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <img src="/images/fsl_logo.png" alt="FSL Logo" style={{ height: '40px' }} />
            </div>
            <button className="btn btn-light rounded-circle" style={{ width: '36px', height: '36px' }} onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              
              {/* Page Title */}
              <div className="text-center mb-4">
                <h1 className="fw-bold" style={{ fontSize: '24px', color: '#111827' }}>
                  Product details
                </h1>
                {agentId && (
                  <div className="mt-2">
                    <span className="badge" style={{ backgroundColor: `${BRAND_COLOR}15`, color: BRAND_COLOR, border: `1px solid ${BRAND_COLOR_BORDER}`, padding: '6px 12px', fontSize: '12px', fontWeight: '500' }}>
                      <i className="fas fa-user-tag me-1"></i>
                      Agent: {agentId.substring(0, 8)}...
                    </span>
                  </div>
                )}
              </div>

              {/* Debug Info - Dev Only */}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-center mb-4">
                  <small className="text-muted">
                    Company: {companyId} | Link: {linkId} | Product: {productId} | Agent: {agentId || 'NOT FOUND'}
                  </small>
                </div>
              )}

              {/* Product Summary Card */}
              <div className="rounded-3 p-4 mb-4" style={{ backgroundColor: 'white', border: `1px solid ${BRAND_COLOR_BORDER}` }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: BRAND_COLOR_LIGHT, color: BRAND_COLOR, width: '48px', height: '48px', fontWeight: '700', fontSize: '18px', border: `2px solid ${BRAND_COLOR_BORDER}` }}>
                      {product.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="fw-bold mb-1" style={{ fontSize: '18px', color: '#111827' }}>{product.name}</h3>
                      <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{product.provider}</p>
                    </div>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>Starting from</small>
                    <div className="fw-bold" style={{ color: BRAND_COLOR, fontSize: '16px' }}>{formatPrice(product.price)} / {product.period}</div>
                  </div>
                </div>
              </div>

              {/* Accordion Sections */}
              <div className="mb-4">
                
                {/* About Product */}
                <div className="rounded-3 mb-2" style={{ backgroundColor: 'white' }}>
                  <div className="p-3 d-flex justify-content-between align-items-center" onClick={() => toggleSection('about')} style={{ cursor: 'pointer' }}>
                    <h6 className="fw-semibold mb-0" style={{ fontSize: '15px', color: '#111827' }}>About product</h6>
                    <i className={`fas fa-chevron-${openSections.about ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                  </div>
                  {openSections.about && (
                    <div className="px-3 pb-3" style={{ borderTop: `1px solid #f3f4f6` }}>
                      <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: '1.6' }}>{product.about}</p>
                    </div>
                  )}
                </div>

                {/* Benefits */}
                <div className="rounded-3 mb-2" style={{ backgroundColor: 'white' }}>
                  <div className="p-3 d-flex justify-content-between align-items-center" onClick={() => toggleSection('benefits')} style={{ cursor: 'pointer' }}>
                    <h6 className="fw-semibold mb-0" style={{ fontSize: '15px', color: '#111827' }}>Benefits</h6>
                    <i className={`fas fa-chevron-${openSections.benefits ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                  </div>
                  {openSections.benefits && (
                    <div className="px-3 pb-3" style={{ borderTop: `1px solid #f3f4f6` }}>
                      <ul className="list-unstyled mb-0">
                        {product.benefits.map((benefit, index) => (
                          <li key={index} className="mb-2 d-flex align-items-start gap-2">
                            <i className="fas fa-check-circle mt-1" style={{ color: BRAND_COLOR, fontSize: '14px', flexShrink: 0 }}></i>
                            <span className="text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* How it Works */}
                <div className="rounded-3 mb-2" style={{ backgroundColor: 'white' }}>
                  <div className="p-3 d-flex justify-content-between align-items-center" onClick={() => toggleSection('howItWorks')} style={{ cursor: 'pointer' }}>
                    <h6 className="fw-semibold mb-0" style={{ fontSize: '15px', color: '#111827' }}>How it works</h6>
                    <i className={`fas fa-chevron-${openSections.howItWorks ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                  </div>
                  {openSections.howItWorks && (
                    <div className="px-3 pb-3" style={{ borderTop: `1px solid #f3f4f6` }}>
                      <ol className="list-unstyled mb-0">
                        {product.howItWorks.map((step, index) => (
                          <li key={index} className="mb-2 d-flex align-items-start gap-2">
                            <span className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ backgroundColor: BRAND_COLOR_LIGHT, color: BRAND_COLOR, width: '24px', height: '24px', fontSize: '12px', fontWeight: '600' }}>{index + 1}</span>
                            <span className="text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>

                {/* Hospitals - Using YOUR Hospitals array format */}
                <div className="rounded-3 mb-2" style={{ backgroundColor: 'white' }}>
                  <div className="p-3 d-flex justify-content-between align-items-center" onClick={() => toggleSection('hospitals')} style={{ cursor: 'pointer' }}>
                    <h6 className="fw-semibold mb-0" style={{ fontSize: '15px', color: '#111827' }}>
                      Hospitals ({product.hospitals.length})
                    </h6>
                    <i className={`fas fa-chevron-${openSections.hospitals ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }}></i>
                  </div>
                  {openSections.hospitals && (
                    <div className="px-3 pb-3" style={{ borderTop: `1px solid #f3f4f6` }}>
                      <ul className="list-unstyled mb-0">
                        {product.hospitals.map((hospital, index) => (
                          <li key={index} className="mb-2 d-flex align-items-start gap-2">
                            <i className="fas fa-hospital mt-1" style={{ color: BRAND_COLOR, fontSize: '14px', flexShrink: 0 }}></i>
                            <span className="text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>{hospital}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

              </div>

              {/* Continue Button */}
              <div className="text-center mt-5">
                <button
                  onClick={handleContinue}
                  disabled={loading}
                  className="btn px-5 py-3 fw-semibold rounded-3"
                  style={{
                    backgroundColor: BRAND_COLOR,
                    color: 'white',
                    border: 'none',
                    minWidth: '300px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = BRAND_COLOR_HOVER; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = BRAND_COLOR; }}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Loading...</>
                  ) : 'Continue'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-top py-3">
        <div className="container text-center">
          <p className="text-muted small mb-0">
            <i className="fas fa-shield-alt me-1" style={{ color: BRAND_COLOR }}></i>
            Powered by <span style={{ fontWeight: '600', color: BRAND_COLOR }}>FSL</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ProductDetail;